import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Tag,
  Button,
  Typography,
  Space,
  Segmented,
  Tooltip,
  message,
  Empty,
} from 'antd';
import { CheckOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { api } from '@/services/api';
import { API_ENDPOINTS } from '@/constants';
import { formatShortDate } from '@/utils/date';
import type { HelpRequest, HelpRequestsResponse, ApiResponse } from '@/types';

const { Title, Text } = Typography;

const PAGE_SIZE = 20;

export default function HelpRequestsPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'OPEN' | 'RESOLVED'>('OPEN');
  const [page, setPage] = useState(1);

  const queryParams = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
  if (statusFilter !== 'ALL') queryParams.set('status', statusFilter);

  const { data, isLoading } = useQuery({
    queryKey: ['help-requests', statusFilter, page],
    queryFn: () =>
      api.get<never, ApiResponse<HelpRequestsResponse>>(
        `${API_ENDPOINTS.HELP_REQUESTS}?${queryParams}`,
      ),
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) =>
      api.patch(`${API_ENDPOINTS.HELP_REQUESTS}/${id}/resolve`, {}),
    onSuccess: () => {
      message.success('Marked as resolved');
      queryClient.invalidateQueries({ queryKey: ['help-requests'] });
    },
    onError: () => message.error('Failed to resolve'),
  });

  const rows = data?.data?.items ?? [];
  const total = data?.data?.total ?? 0;

  const columns: ColumnsType<HelpRequest> = [
    {
      title: 'Student',
      dataIndex: ['student', 'name'],
      key: 'student',
      render: (_: unknown, record: HelpRequest) => (
        <div>
          <Text strong style={{ display: 'block', fontSize: 13 }}>
            {record.student?.name ?? '—'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.student?.email ? record.student.email : '—'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Deck / Card',
      key: 'card',
      render: (_: unknown, record: HelpRequest) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>
            {record.deck?.topicName ?? '—'}
          </Text>
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
            {record.card?.title ?? record.card?.frontText?.slice(0, 60) ?? '—'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      render: (note: string | null) =>
        note ? (
          <Text style={{ fontSize: 13 }}>{note}</Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            No note provided
          </Text>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) =>
        status === 'OPEN' ? (
          <Tag color="orange">Open</Tag>
        ) : (
          <Tag color="green">Resolved</Tag>
        ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      render: (v: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatShortDate(v)}
        </Text>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_: unknown, record: HelpRequest) =>
        record.status === 'OPEN' ? (
          <Tooltip title="Mark as resolved">
            <Button
              size="small"
              icon={<CheckOutlined />}
              onClick={() => resolveMutation.mutate(record.id)}
              loading={resolveMutation.isPending}
              style={{ borderRadius: 6 }}
            >
              Resolve
            </Button>
          </Tooltip>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Resolved
          </Text>
        ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <QuestionCircleOutlined style={{ fontSize: 20, color: '#f59e0b' }} />
          <Title level={3} style={{ marginBottom: 0 }}>
            Help Requests
          </Title>
        </div>
        <Text type="secondary" style={{ fontSize: 15 }}>
          Cards that students flagged as confusing during review.
        </Text>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Segmented
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v as 'ALL' | 'OPEN' | 'RESOLVED'); setPage(1); }}
            options={[
              { label: 'Open', value: 'OPEN' },
              { label: 'Resolved', value: 'RESOLVED' },
              { label: 'All', value: 'ALL' },
            ]}
          />
          <Text type="secondary" style={{ fontSize: 13 }}>
            {total} request{total !== 1 ? 's' : ''}
          </Text>
        </Space>
      </div>

      <Table
        dataSource={rows}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        locale={{ emptyText: <Empty description="No help requests" /> }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
        style={{ borderRadius: 12, overflow: 'hidden' }}
      />
    </div>
  );
}
